/**
 * Admin page to manually convert existing images to WebP
 */
import { useState } from 'react';
import { convertImageToWebP, formatFileSize } from '@/services/imageOptimizationService';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { Button } from '@/components/ui/Button';
import { toast } from '@/lib/toast';

interface ConversionResult {
  filePath: string;
  success: boolean;
  originalSize?: number;
  webpSize?: number;
  compressionRatio?: string;
  error?: string;
}

export default function ImageOptimizationAdmin() {
  const [filePath, setFilePath] = useState('');
  const [converting, setConverting] = useState(false);
  const [results, setResults] = useState<ConversionResult[]>([]);
  const [batchProgress, setBatchProgress] = useState<string>('');

  const handleConvert = async () => {
    if (!filePath.trim()) {
      toast.error('Por favor ingresa la ruta del archivo');
      return;
    }

    setConverting(true);

    try {
      const result = await convertImageToWebP(filePath);

      const conversionResult: ConversionResult = {
        filePath,
        success: true,
        originalSize: result.originalSize,
        webpSize: result.webpSize,
        compressionRatio: result.compressionRatio,
      };

      setResults(prev => [conversionResult, ...prev]);
      toast.success(`Imagen convertida exitosamente! Ahorro: ${result.compressionRatio}`);
      setFilePath(''); // Clear input after success
    } catch (error: any) {
      const conversionResult: ConversionResult = {
        filePath,
        success: false,
        error: error.message || 'Error desconocido',
      };

      setResults(prev => [conversionResult, ...prev]);
      toast.error(`Error: ${error.message}`);
    } finally {
      setConverting(false);
    }
  };

  const handleConvertAll = async () => {
    setConverting(true);
    setBatchProgress('Iniciando conversión masiva...');
    toast.info('Conversión masiva iniciada. Esto puede tardar varios minutos.');

    try {
      const convertAll = httpsCallable<{}, {
        success: boolean;
        summary: {
          total: number;
          converted: number;
          skipped: number;
          failed: number;
        };
        errors: string[];
        conversions: any[];
      }>(functions, 'convertAllImagesToWebP');

      setBatchProgress('Ejecutando conversión... Por favor espera.');

      const result = await convertAll({});

      setBatchProgress('');

      if (result.data.success) {
        const { summary, conversions } = result.data;

        // Agregar todas las conversiones exitosas a los resultados
        conversions.forEach((conv: any) => {
          setResults(prev => [{
            filePath: conv.filePath,
            success: true,
            originalSize: conv.originalSize,
            webpSize: conv.webpSize,
            compressionRatio: conv.compressionRatio,
          }, ...prev]);
        });

        toast.success(
          `¡Completado! ${summary.converted} imágenes convertidas, ${summary.skipped} omitidas, ${summary.failed} fallaron.`
        );

        console.log('Resumen completo:', result.data);
      }
    } catch (error: any) {
      setBatchProgress('');
      toast.error(`Error en conversión masiva: ${error.message}`);
      console.error('Error:', error);
    } finally {
      setConverting(false);
    }
  };

  const handleDeleteOriginals = async () => {
    setConverting(true);
    setBatchProgress('Eliminando imágenes originales...');
    toast.info('Iniciando eliminación de imágenes originales.');

    try {
      const deleteOriginals = httpsCallable<{}, {
        success: boolean;
        summary: {
          total: number;
          deleted: number;
          skipped: number;
        };
        errors: string[];
      }>(functions, 'deleteOriginalImages');

      const result = await deleteOriginals({});

      setBatchProgress('');

      if (result.data.success) {
        const { summary } = result.data;

        toast.success(
          `✅ Completado! ${summary.deleted} imágenes eliminadas, ${summary.skipped} omitidas.`
        );

        console.log('Resumen completo:', result.data);
      }
    } catch (error: any) {
      setBatchProgress('');
      toast.error(`Error eliminando imágenes: ${error.message}`);
      console.error('Error:', error);
    } finally {
      setConverting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-2 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-ink mb-2">
            🖼️ Optimización de Imágenes
          </h1>
          <p className="text-ink-muted">
            Convierte imágenes existentes en Firebase Storage a formato WebP
          </p>
        </div>

        {/* Single Image Conversion */}
        <div className="bg-surface rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-ink mb-4">
            Convertir Imagen Individual
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Ruta del archivo en Storage
              </label>
              <input
                type="text"
                value={filePath}
                onChange={(e) => setFilePath(e.target.value)}
                placeholder="services/abc123/image.png"
                className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus bg-surface text-ink"
                disabled={converting}
              />
              <p className="text-sm text-ink-subtle mt-1">
                Ejemplo: services/lILDGxMgfsifzpqg4YZu/1762356562409_tratamiento_capilar.png
              </p>
            </div>

            <Button
              onClick={handleConvert}
              disabled={converting || !filePath.trim()}
              className="w-full"
            >
              {converting ? 'Convirtiendo...' : 'Convertir a WebP'}
            </Button>
          </div>
        </div>

        {/* Convert All Images */}
        <div className="rounded-lg shadow-md p-6 mb-6 border-2 border-accent/30">
          <div className="flex items-start gap-3 mb-4">
            <span className="text-3xl">🚀</span>
            <div>
              <h2 className="text-xl font-semibold text-ink mb-2">
                Conversión Masiva Automática
              </h2>
              <p className="text-ink-muted mb-2">
                Convierte TODAS las imágenes existentes en Firebase Storage a WebP con un solo clic.
              </p>
              <ul className="text-sm text-ink-muted space-y-1 list-disc list-inside">
                <li>Escanea automáticamente todo Firebase Storage</li>
                <li>Detecta y omite archivos ya convertidos</li>
                <li>No elimina los originales</li>
                <li>Puede tardar varios minutos dependiendo de cuántas imágenes tengas</li>
              </ul>
            </div>
          </div>

          {batchProgress && (
            <div className="mb-4 p-3 bg-info/10 rounded-lg text-info text-sm">
              ⏳ {batchProgress}
            </div>
          )}

          <Button
            onClick={handleConvertAll}
            disabled={converting}
            className="w-full text-black font-bold"
          >
            {converting ? '🔄 Convirtiendo todas las imágenes...' : '🚀 Convertir TODAS las Imágenes'}
          </Button>
        </div>

        {/* Delete Original Images */}
        <div className="rounded-lg shadow-md p-6 mb-6 border-2 border-danger/30">
          <div className="flex items-start gap-3 mb-4">
            <span className="text-3xl">🗑️</span>
            <div>
              <h2 className="text-xl font-semibold text-ink mb-2">
                Eliminar Imágenes Originales (PNG/JPG)
              </h2>
              <p className="text-ink-muted mb-2">
                ⚠️ <strong>PELIGRO:</strong> Elimina TODAS las imágenes PNG/JPG originales que ya tienen versión WebP.
              </p>
              <ul className="text-sm text-ink-muted space-y-1 list-disc list-inside">
                <li>Solo elimina imágenes que tienen versión WebP</li>
                <li><strong>Esta acción NO SE PUEDE DESHACER</strong></li>
                <li>Asegúrate de que todas las conversiones funcionaron correctamente</li>
                <li>Ahorra espacio en Storage (reduce costos)</li>
              </ul>
            </div>
          </div>

          {batchProgress && (
            <div className="mb-4 p-3 bg-info/10 rounded-lg text-info text-sm">
              ⏳ {batchProgress}
            </div>
          )}

          <Button
            onClick={handleDeleteOriginals}
            disabled={converting}
            className="w-full text-white font-bold"
          >
            {converting ? '🔄 Eliminando imágenes originales...' : '🗑️ Eliminar Imágenes Originales (PNG/JPG)'}
          </Button>
        </div>

        {/* Results */}
        {results.length > 0 && (
          <div className="bg-surface rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-ink mb-4">
              Resultados ({results.length})
            </h2>

            <div className="space-y-3">
              {results.map((result, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border ${
                    result.success
                      ? 'bg-success/10 border-success/30'
                      : 'bg-danger/10 border-danger/30'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">
                          {result.success ? '✅' : '❌'}
                        </span>
                        <code className="text-sm text-ink-muted break-all">
                          {result.filePath}
                        </code>
                      </div>

                      {result.success ? (
                        <div className="text-sm text-ink-muted ml-7">
                          <div>
                            Original: {formatFileSize(result.originalSize!)} →
                            WebP: {formatFileSize(result.webpSize!)}
                          </div>
                          <div className="font-semibold text-success">
                            Ahorro: {result.compressionRatio}
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-danger ml-7">
                          {result.error}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info */}
        <div className="mt-6 bg-info/10 rounded-lg p-4 border border-info/30">
          <h3 className="text-sm font-semibold text-info mb-2">
            💡 Información
          </h3>
          <ul className="text-sm text-info space-y-1 list-disc list-inside">
            <li>Las imágenes originales NO se eliminan</li>
            <li>Se crea una versión .webp junto a la original</li>
            <li>Los archivos SVG no necesitan conversión</li>
            <li>Las nuevas imágenes se convierten automáticamente al subirlas</li>
            <li>Debes estar autenticado para usar esta herramienta</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
